<template>
  <v-card variant="outlined" elevation="0" class="sett-summary-card !bg-[rgb(var(--v-theme-surface))]">
    <v-card-title class="text-subtitle-1 text-center" style="padding: 14px 18px; border-bottom: 1px solid #eee;">
      Settlement Summary
    </v-card-title>
    <v-card-text style="padding: 16px 18px;">
      <template v-if="hasSummaryData">

        <!-- ═══ Recovery Amount (all variants) ═══ -->
        <SummaryRow label="Recovery Amount">
          <template #badge>
            <span v-if="hasStep1" class="text-xs ml-1">
              <LlIcon :icon="isFullyComplete ? 'mdi-lock' : 'mdi-check'" size="12" :color="isFullyComplete ? 'grey-darken-1' : 'success'" />
            </span>
          </template>
          <template #value>
            <span class="text-body-2 font-weight-bold">
              {{ displayRecoveryAmount > 0 ? formatCurrency(displayRecoveryAmount) : '—' }}
            </span>
          </template>
        </SummaryRow>

        <!-- ═══ VARIANT: Absorbed + Net ═══ -->
        <template v-if="isAbsorbed && isNet">
          <!-- Sub-row: absorbed costs — informational, NOT a client deduction. Under either Absorbed
               arrangement the client is never charged these (backend unreimbursedExpenses = 0), so
               rendering this full-weight and negative made the column read ~$10k lower than the
               Client Net Recovery actually shown below (for the canonical $100k/33.33%/$10k case it
               read as $55,003 — the FirmAdvances answer — against a displayed $65,003). Same muted
               treatment as the Absorbed+Gross block, so both variants now reconcile identically:
               only Attorney Fee and Liens are full-weight deductions. -->
          <div class="flex justify-between py-1">
            <span class="text-caption text-grey-darken-1">Less: {{ costLabel }} (absorbed)</span>
            <span
              class="text-caption text-grey-darken-1"
              :class="redesignEnabled ? { 'font-italic': !isExpensesConfirmed } : { 'text-grey-lighten-1': !isExpensesConfirmed }"
            >
              {{ formatCurrency(netExpenses ?? 0) }}
            </span>
          </div>

          <!-- Net for Fee Calc subtotal (visible only when step 2 done) -->
          <div v-if="hasStep2 && breakdown?.adjustedRecovery != null" class="flex justify-between py-1 px-1 bg-grey-lighten-4 rounded">
            <span class="text-body-2 font-weight-bold">Net for Fee Calc</span>
            <span class="text-body-2 font-weight-bold">{{ formatCurrency(breakdown.adjustedRecovery) }}</span>
          </div>

          <SummaryRow :label="`Attorney Fee (${feePercentageLabel})`">
            <template #badge>
              <span v-if="isAttorneyFeeConfirmed" class="text-xs ml-1">
                <LlIcon :icon="isFullyComplete ? 'mdi-lock' : 'mdi-check'" size="12" :color="isFullyComplete ? 'grey-darken-1' : 'success'" />
              </span>
            </template>
            <template #value>

              <span class="text-body-2" :class="emphasisClass(isAttorneyFeeConfirmed, hasStep3)">
                {{ formatFeeValue(displayedFee) }}
              </span>
            </template>
          </SummaryRow>

          <!-- Sub-row: net attorney fee — the amount actually recorded/invoiced (BR-24). Muted and
               informational only: Client Net Recovery below reconciles against Attorney Fee above,
               not this — promoting it to full weight would visually disagree with the Client Net
               Recovery total by exactly the absorbed amount. Rendered unsigned (it's what the firm
               receives, not a deduction from the client), but NOT via Math.abs — when absorbed costs
               exceed the fee this legitimately goes negative and formatCurrency's ($1,234.00)
               treatment must survive, or an underwater firm reads as if it kept the money. -->
          <div class="flex justify-between py-1">
            <span class="text-caption text-grey-darken-1">Net Attorney Fee</span>
            <span
              class="text-caption text-grey-darken-1"
              :class="redesignEnabled ? { 'font-italic': !isAttorneyFeeConfirmed } : { 'text-grey-lighten-1': !isAttorneyFeeConfirmed }"
            >
              {{ displayedNetAttorneyFee != null ? formatCurrency(displayedNetAttorneyFee) : '—' }}
            </span>
          </div>
        </template>

        <!-- ═══ VARIANT: Absorbed + Gross ═══ -->
        <template v-else-if="isAbsorbed && !isNet">
          <SummaryRow :label="`Gross Fee (${feePercentageLabel})`">
            <template #value>

              <span class="text-body-2" :class="emphasisClass(isAttorneyFeeConfirmed, hasStep3)">
                {{ formatFeeValue(displayedFee) }}
              </span>
            </template>
          </SummaryRow>

          <!-- Sub-row: expenses absorbed (smaller font, always muted — a secondary breakdown
               detail under Gross Fee, never promoted to bold/black even once confirmed) -->
          <div class="flex justify-between py-1">
            <span class="text-caption text-grey-darken-1">Less: {{ costLabel }} (absorbed)</span>
            <span
              class="text-caption text-grey-darken-1"
              :class="redesignEnabled ? { 'font-italic': !isExpensesConfirmed } : { 'text-grey-lighten-1': !isExpensesConfirmed }"
            >
              {{ formatCurrency(netExpenses ?? 0) }}
            </span>
          </div>

          <!-- Sub-row: net attorney fee — informational only, same treatment as the absorbed-costs
               sub-row above (Client Net Recovery reconciles against Gross Fee, not this). Sign is
               preserved deliberately — see the matching row in the Absorbed+Net block above. -->
          <div class="flex justify-between py-1">
            <span class="text-caption text-grey-darken-1">Net Attorney Fee</span>
            <span
              class="text-caption text-grey-darken-1"
              :class="redesignEnabled ? { 'font-italic': !isAttorneyFeeConfirmed } : { 'text-grey-lighten-1': !isAttorneyFeeConfirmed }"
            >
              {{ displayedNetAttorneyFee != null ? formatCurrency(displayedNetAttorneyFee) : '—' }}
            </span>
          </div>
        </template>

        <!-- ═══ VARIANT: Net (non-absorbed) ═══ -->
        <template v-else-if="isNet">
          <SummaryRow :label="`Less: ${costLabel}`">
            <template #badge>
              <span v-if="isExpensesConfirmed" class="text-xs ml-1">
                <LlIcon :icon="isFullyComplete ? 'mdi-lock' : 'mdi-check'" size="12" :color="isFullyComplete ? 'grey-darken-1' : 'success'" />
              </span>
            </template>
            <template #value>
              <span class="text-body-2" :class="emphasisClass(isExpensesConfirmed, hasStep2)">
                {{ formatCurrency(-Math.abs(netExpenses ?? 0)) }}
              </span>
            </template>
          </SummaryRow>

          <!-- ClientPays sub-row: grouped directly under Case Costs, not after the fee -->
          <div v-if="hasStep2 && isClientPays && (breakdown?.alreadyPaidByClient ?? 0) > 0" class="flex justify-between py-1">
            <span class="text-caption text-grey-darken-1">Already paid by client</span>
            <span class="text-caption text-grey-darken-1">{{ formatCurrency(breakdown.alreadyPaidByClient) }}</span>
          </div>

          <SummaryRow v-if="hasStep2 && (breakdown?.expenseInterest ?? 0) > 0" label="Less: Expense Interest">
            <template #value>
              <span class="text-body-2 font-weight-bold">{{ formatCurrency(-breakdown.expenseInterest) }}</span>
            </template>
          </SummaryRow>

          <!-- Net for Fee Calc subtotal (visible only when step 2 done) -->
          <div v-if="hasStep2 && breakdown?.adjustedRecovery != null" class="flex justify-between py-1 px-1 bg-grey-lighten-4 rounded">
            <span class="text-body-2 font-weight-bold">Net for Fee Calc</span>
            <span class="text-body-2 font-weight-bold">{{ formatCurrency(breakdown.adjustedRecovery) }}</span>
          </div>

          <SummaryRow :label="`Attorney Fee (${feePercentageLabel})`">
            <template #badge>
              <span v-if="isAttorneyFeeConfirmed" class="text-xs ml-1">
                <LlIcon :icon="isFullyComplete ? 'mdi-lock' : 'mdi-check'" size="12" :color="isFullyComplete ? 'grey-darken-1' : 'success'" />
              </span>
            </template>
            <template #value>

              <span class="text-body-2" :class="emphasisClass(isAttorneyFeeConfirmed, hasStep3)">
                {{ formatFeeValue(displayedFee) }}
              </span>
            </template>
          </SummaryRow>
        </template>

        <!-- ═══ VARIANT: Gross (non-absorbed) — default ═══ -->
        <template v-else>
          <SummaryRow :label="`Attorney Fee (${feePercentageLabel})`">
            <template #badge>
              <span v-if="isAttorneyFeeConfirmed" class="text-xs ml-1">
                <LlIcon :icon="isFullyComplete ? 'mdi-lock' : 'mdi-check'" size="12" :color="isFullyComplete ? 'grey-darken-1' : 'success'" />
              </span>
            </template>
            <template #value>

              <span class="text-body-2" :class="emphasisClass(isAttorneyFeeConfirmed, hasStep3)">
                {{ formatFeeValue(displayedFee) }}
              </span>
            </template>
          </SummaryRow>

          <SummaryRow :label="`Less: ${costLabel}`">
            <template #badge>
              <span v-if="isExpensesConfirmed" class="text-xs ml-1">
                <LlIcon :icon="isFullyComplete ? 'mdi-lock' : 'mdi-check'" size="12" :color="isFullyComplete ? 'grey-darken-1' : 'success'" />
              </span>
            </template>
            <template #value>
              <span class="text-body-2" :class="emphasisClass(isExpensesConfirmed, hasStep2)">
                {{ formatCurrency(-Math.abs(netExpenses ?? 0)) }}
              </span>
            </template>
          </SummaryRow>

          <!-- ClientPays sub-row: grouped directly under Case Costs -->
          <div v-if="hasStep2 && isClientPays && (breakdown?.alreadyPaidByClient ?? 0) > 0" class="flex justify-between py-1">
            <span class="text-caption text-grey-darken-1">Already paid by client</span>
            <span class="text-caption text-grey-darken-1">{{ formatCurrency(breakdown.alreadyPaidByClient) }}</span>
          </div>
        </template>

        <!-- ═══ Expense Interest (FirmAdvances only — zero/absent for other arrangements) ═══
             Net variants render their own copy of this row above "Net for Fee Calc" instead
             (sc-46420) — this bottom instance only fires for Gross, which has no such subtotal
             for the row to visually disagree with. -->
        <SummaryRow v-if="hasStep2 && !isNet && (breakdown?.expenseInterest ?? 0) > 0" label="Less: Expense Interest">
          <template #value>
            <span class="text-body-2 font-weight-bold">{{ formatCurrency(-breakdown.expenseInterest) }}</span>
          </template>
        </SummaryRow>

        <!-- ═══ Sales Tax (all variants — captured from QuickBooks at invoice finalize) ═══
             A Canadian firm's tax splits into GST + PST/QST so this matches the statement PDF;
             everyone else keeps the single lumped row. -->
        <template v-if="(breakdown?.salesTax ?? 0) > 0">
          <SummaryRow
            v-for="component in breakdown?.taxComponents ?? []"
            :key="component.name"
            :label="`Less: ${component.name}`"
          >
            <template #value>
              <span class="text-body-2 font-weight-bold">{{ formatCurrency(-component.amount) }}</span>
            </template>
          </SummaryRow>
          <SummaryRow v-if="!(breakdown?.taxComponents ?? []).length" label="Less: Sales Tax">
            <template #value>
              <span class="text-body-2 font-weight-bold">{{ formatCurrency(-breakdown.salesTax) }}</span>
            </template>
          </SummaryRow>
        </template>

        <!-- ═══ Liens (all variants) ═══ -->
        <SummaryRow label="Less: Third-Party Liens">
          <template #badge>
            <span v-if="isLiensConfirmed" class="text-xs ml-1">
              <LlIcon :icon="isFullyComplete ? 'mdi-lock' : 'mdi-check'" size="12" :color="isFullyComplete ? 'grey-darken-1' : 'success'" />
            </span>
          </template>
          <template #value>
            <span class="text-body-2" :class="emphasisClass(isLiensConfirmed, hasStep4)">
              {{ formatCurrency(-(lienTotal ?? 0)) }}
            </span>
          </template>
        </SummaryRow>

        <!-- ═══ Client Net Recovery (all variants) ═══ -->
        <v-divider class="my-2" />
        <div class="flex justify-between py-1">
          <span class="text-body-2 font-weight-bold text-grey-darken-2">
            Client Net Recovery
          </span>
          <span class="flex items-center gap-1">
            <span class="text-body-1" :class="clientNetRecoveryClass">
              {{ displayedClientNet != null ? formatCurrency(displayedClientNet) : '—' }}
            </span>
            <LlTooltip
              v-if="!isClientNetRecoveryConfirmed && isClientNetRecoveryNegativeOrZero"
              icon="warning"
              text="Client net recovery is zero or negative — review expenses and liens before proceeding."
            />
          </span>
        </div>


        <!-- Estimates footnote — redesign only; only when there are unconfirmed (italic) rows -->
        <!-- <div v-if="redesignEnabled && hasSummaryData && !isFullyComplete" class="text-caption text-grey-darken-1 mt-1 sett-estimates-footnote">
          Italic values are estimates.
        </div> -->

        <v-divider class="my-2" />
        <div class="text-body-2 text-center" v-if="generatedDateFormatted">
          <span class="text-body-2 text-grey-darken-1">Statement</span>
          <div class="text-body-2 font-weight-medium text-green-darken-2 mt-1">
            <LlIcon icon="mdi-check" size="14" color="success" class="mr-1" />
            Generated {{ generatedDateFormatted }}
          </div>
        </div>

      </template>

      <div v-else class="text-body-2 text-grey text-center py-4">
        Complete each step to build your settlement
        breakdown.
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { formatCurrency, formatLongDateString } from '@/services/utils';
import LlTooltip from '@/components/LlTooltip.vue';
import LlIcon from '@/components/LlIcon.vue';
import type { ComputedBreakdown } from '@/models/settlementTypes';
import { FEE_METHOD, EXPENSE_ARRANGEMENT } from '@/constants/settlementConstants';
import SummaryRow from './SettlementSummaryRow.vue';

const props = defineProps({
  hasSummaryData: { type: Boolean, default: false },
  displayRecoveryAmount: { type: Number, default: 0 },
  feePercentage: { type: Number as () => number | null, default: null },
  breakdown: { type: Object as () => ComputedBreakdown | null, default: null },
  lienTotal: { type: Number, default: 0 },
  completedSteps: { type: Array as () => number[], default: (): number[] => [] },
  feeMethod: { type: String as () => string | null, default: null },
  expenseArrangement: { type: String as () => string | null, default: null },
  /** Mirrors SettlementCalculationService's RoundAmount so the pre-confirmation predicted fee
   *  below lands on the same value the backend will produce — otherwise a firm with this setting
   *  on would see the prediction snap from cents to whole dollars the moment Step 1 is confirmed. */
  roundToNearestDollar: { type: Boolean, default: false },
  isFullyComplete: { type: Boolean, default: false },
  generatedDate: { type: String, default: null },
  /** Gate redesign styles (italic estimates, dark-grey unconfirmed text, estimates footnote).
   *  false = old design (grey-lighten-1 unconfirmed, no footnote). */
  redesignEnabled: { type: Boolean, default: false },
  // SC-45815 — backend-derived confirmed/estimated flags (Is*Estimated inverted by the store's
  // mapApiToFrontend). Pre-population data itself always flows regardless of redesignEnabled;
  // only the italic/bold styling below is gated on it.
  isExpensesConfirmed: { type: Boolean, default: false },
  isLiensConfirmed: { type: Boolean, default: false },
  isAttorneyFeeConfirmed: { type: Boolean, default: false },
  isClientNetRecoveryConfirmed: { type: Boolean, default: false },
});

// Step-completion shorthand
const hasStep1 = computed(() => props.completedSteps.includes(1));
const hasStep2 = computed(() => props.completedSteps.includes(2));
const hasStep3 = computed(() => props.completedSteps.includes(3));
const hasStep4 = computed(() => props.completedSteps.includes(4));

// Variant helpers
const isNet = computed(() => props.feeMethod === FEE_METHOD.NET);
const isAbsorbed = computed(() => props.expenseArrangement === EXPENSE_ARRANGEMENT.ABSORBED);
const isClientPays = computed(() => props.expenseArrangement === EXPENSE_ARRANGEMENT.CLIENT);
const costLabel = computed(() => (isClientPays.value ? 'Case Costs' : 'Expenses Advanced'));

// 0% is a valid configured contingency (pro bono) — must read as "0%", not fall through to the
// unconfigured "—" that null gets below.
const feePercentageLabel = computed(() => (props.feePercentage != null ? `${props.feePercentage}%` : '—'));

// totalExpenses includes expense interest folded in (see backend PersistCalculationSnapshots) —
// subtract it back out here so the "Less: Expenses" line and the standalone "Less: Expense
// Interest" line don't double-count. No-op when there's no interest (most settlements).
// Co-counsel expenses are added in separately — the backend keeps them out of totalExpenses
// and reports them via coCounselExpensesTotal instead (see ApiSettlementSummary).
const netExpenses = computed(() => {
  if (props.breakdown?.totalExpenses == null) return null;
  return (
    props.breakdown.totalExpenses -
    (props.breakdown?.expenseInterest ?? 0) +
    (props.breakdown?.coCounselExpensesTotal ?? 0)
  );
});

// Unlike Expenses/Liens (always a real number — $0.00 when none exist, per the ticket), Attorney
// Fee has no matter-level "unconfirmed" data source: pre-confirmation it's either an existing
// settlement-fee charge (a real number) or nothing at all (blank, not $0.00) — the ticket's own
// wording is "existing settlement fee if one exists; otherwise blank". Applies to `fee` and
// `netAttorneyFee` alike — both stay null until Calculate() has something to derive them from.
function formatFeeValue(value: number | null | undefined): string {
  return value == null ? '—' : formatCurrency(-Math.abs(value));
}

// Pre-Step-1 prediction: mirrors SettlementCalculationService.cs's core formula so Attorney Fee /
// Net Attorney Fee / Client Net Recovery never sit blank just because Recovery Amount hasn't been
// confirmed yet — every input here is already live (selected-deposit total, live expense/lien
// estimates, configured %). Deliberately approximate: skips co-counsel splits (not yet live in
// production) and roundFeeToNearestDollar. Only ever used as a fallback when the backend hasn't
// supplied a real value (breakdown?.fee/clientNet null) — once Recovery Amount is confirmed, the
// backend's authoritative Calculate() result takes over immediately and this computed naturally
// returns null via its own guard clause.
const predictedAttorneyFee = computed(() => {
  if (props.breakdown?.fee != null) return null;
  if (props.feePercentage == null || props.displayRecoveryAmount <= 0) return null;
  const feeBase = props.feeMethod === FEE_METHOD.NET
    ? props.displayRecoveryAmount - (props.breakdown?.totalExpenses ?? 0)
    : props.displayRecoveryAmount;
  // Matches SettlementCalculationService.RoundAmount: whole dollars when the firm has that
  // setting on, 2dp otherwise. (JS Math.round is half-up, same as .NET AwayFromZero for the
  // positive values a fee always takes.) Both branches keep the multiply in one step rather
  // than dividing then re-multiplying, so no float drift creeps into the cents case.
  return props.roundToNearestDollar
    ? Math.round((feeBase * props.feePercentage) / 100)
    : Math.round(feeBase * props.feePercentage) / 100;
});

// Both fee methods get a Net Attorney Fee under Absorbed now (SC-46217/BR-24 fee-reduction fix) —
// the recorded/invoiced amount is always the fee minus the absorbed costs.
const predictedNetAttorneyFee = computed(() => {
  if (props.breakdown?.netAttorneyFee != null || predictedAttorneyFee.value == null) return null;
  if (!isAbsorbed.value) return null;
  return predictedAttorneyFee.value - (props.breakdown?.totalExpenses ?? 0);
});

const predictedClientNetRecovery = computed(() => {
  if (props.breakdown?.clientNet != null) return null;
  const fee = props.breakdown?.fee ?? predictedAttorneyFee.value;
  if (fee == null) return null;
  const expenses = props.breakdown?.totalExpenses ?? 0;
  const liens = props.lienTotal ?? 0;
  // Mirrors SettlementCalculationService: since SC-46217/BR-24, both Absorbed arrangements share
  // the same shape — the firm's reduced take-home (netAttorneyFee) never affects the client, so
  // there is no separate expense term: recovery − fee − liens. Everything else (Firm/Client
  // advances) — the client bears the costs directly: recovery − fee − expenses − liens.
  if (isAbsorbed.value) {
    return props.displayRecoveryAmount - fee - liens;
  }
  return props.displayRecoveryAmount - fee - expenses - liens;
});

const displayedFee = computed(() => props.breakdown?.fee ?? predictedAttorneyFee.value);
const displayedNetAttorneyFee = computed(() => props.breakdown?.netAttorneyFee ?? predictedNetAttorneyFee.value);
const displayedClientNet = computed(() => props.breakdown?.clientNet ?? predictedClientNetRecovery.value);

/**
 * Redesign: mutually-exclusive bold/black (confirmed) vs italic/gray (estimate) per SC-45815.
 * Legacy: unchanged from pre-SC-45815 behavior — always bold, only the color dims when unconfirmed.
 */
function emphasisClass(confirmed: boolean, legacyHasStep: boolean): string {
  if (props.redesignEnabled) {
    return confirmed ? 'font-weight-bold' : 'font-italic text-grey-darken-1';
  }
  return legacyHasStep ? 'font-weight-bold' : 'font-weight-bold text-grey-lighten-1';
}

// Must NOT fire on a merely-unavailable value (displayedClientNet null — e.g. no contingency %
// configured yet, and not enough live inputs to predict from either): that's "nothing to show"
// (blank, neutral), not "a bad outcome" — falling back to `?? 0` here would show the negative/red
// warning tooltip for data that was simply never computed or predicted.
const isClientNetRecoveryNegativeOrZero = computed(
  () => displayedClientNet.value != null && displayedClientNet.value <= 0,
);

const clientNetRecoveryClass = computed(() => {
  if (props.isClientNetRecoveryConfirmed) {
    return (displayedClientNet.value ?? 0) < 0
      ? 'font-weight-bold text-error'
      : 'font-weight-bold text-green-darken-2';
  }
  return isClientNetRecoveryNegativeOrZero.value
    ? 'font-italic text-error'
    : 'font-italic text-grey-darken-1';
});

// Generated date formatter
const generatedDateFormatted = computed(() => {
  if (!props.generatedDate) return '';
  return formatLongDateString(props.generatedDate) || props.generatedDate;
});
</script>

<style scoped>
.sett-estimates-footnote {
  font-style: italic;
}
</style>
